import ModelTypeConstants from 'constants/ModelTypeConstants';
import {FieldDefinitionType, TagSchemaItemType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {tagSchemaItemTypeToFieldDefinitionType} from 'schemas/fields/FieldDefinitionCompatibilityUtils';

/**
 * Shared "category" mapping for procedure material types.
 *
 * Procedure materials can be constrained three ways — a specific named schema, an entity-type
 * ("Any DNA Sequence", carried as a `folderItemType`), or a fieldset (custom schema interface,
 * carrying a `TagSchemaItemType`). To decide whether two constraints are the "same category" (and to
 * derive the field definition a material projects into the processes platform), we normalize all of
 * them to a `FieldDefinitionType`.
 *
 * The folderItemType helpers here are procedure-specific; the underlying
 * `tagSchemaItemTypeToFieldDefinitionType` mapping is owned by
 * `schemas/fields/FieldDefinitionCompatibilityUtils.ts` and re-exported below. Consumed by:
 *   - `ProcedureFieldDefinitionUtils` (converts a material into a processes `SchemaLinkFieldDefinition`)
 *   - `materialEdgeUtils` (UI edge compatibility — compares two materials by category)
 */

/**
 * Maps a `folderItemType` (lowercase model type, e.g. "sequence") to a representative
 * `TagSchemaItemType`. Multiple `TagSchemaItemType`s within a folderItemType category all resolve to
 * the same `FieldDefinitionType`, so any representative value is correct for category purposes.
 */
export function folderItemTypeToTagSchemaItemType(folderItemType: string): TagSchemaItemType {
  switch (folderItemType) {
    case ModelTypeConstants.SEQUENCE:
      return TagSchemaItemType.DNA_SEQUENCE;
    case ModelTypeConstants.PROTEIN:
      return TagSchemaItemType.AA_SEQUENCE;
    case ModelTypeConstants.MIXTURE:
      return TagSchemaItemType.MIXTURE;
    case ModelTypeConstants.MOLECULE:
      return TagSchemaItemType.MOLECULE;
    case ModelTypeConstants.ENTRY:
      return TagSchemaItemType.ENTRY;
    case ModelTypeConstants.BASIC_FOLDER_ITEM:
    default:
      return TagSchemaItemType.BASIC_FOLDER_ITEM;
  }
}

// `tagSchemaItemTypeToFieldDefinitionType` lives in `FieldDefinitionCompatibilityUtils` (the
// backend-mirrored validation copy); re-exported here so procedure material-type callers have a single
// import surface for the category mapping.
export {tagSchemaItemTypeToFieldDefinitionType};

/** Convenience: maps a `folderItemType` straight to its category `FieldDefinitionType`. */
export function folderItemTypeToFieldDefinitionType(folderItemType: string): FieldDefinitionType {
  return tagSchemaItemTypeToFieldDefinitionType(folderItemTypeToTagSchemaItemType(folderItemType));
}
